import React, { useEffect, useMemo, useRef, useState } from "react";
import { useResponsive } from "../../hook/useResponsive";
import LoadingV2 from "../../components/LoadingV2";
import { DropdownSearch } from "../../components/DropdownSearch";
import PlusIcon from "../../images/PlusIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import BackIcon from "../../images/BackIcon";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { getLocations } from "../../services/locations.services";
import { extractValues, updateUrlParams } from "../../validation/ValidationInput";
import TagStatus from "../../components/TagStatus";
import { formatDateTime } from "../TableReservations/reservationShared";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
} from "../../components/list";
import {
  ReservationSettingsPayload,
  ReservationOperatingHoursPayload,
  ReservationBlockPeriodPayload,
  createReservationSettings,
  getReservationSettingsList,
  updateReservationSettings,
  deleteReservationSettings,
  createReservationOperatingHours,
  getReservationOperatingHoursList,
  updateReservationOperatingHours,
  deleteReservationOperatingHours,
  createReservationBlockPeriod,
  getReservationBlockPeriodsList,
  updateReservationBlockPeriod,
  deleteReservationBlockPeriod,
} from "../../services/reservation.service";

/**
 * Standalone "Reservations Setup" module (sibling of Floor Plans / Sale
 * Points under the "Company" sidebar group). Routed at `/reservations` and
 * gated by the `Reservation Setups` permission via `dev-core-web`
 * `ProtectedRoute`.
 *
 * Three sub-sections, each backed by its own back-office endpoint:
 *   1. Settings           — reservations_settings        (one row per company+location)
 *   2. Operating hours    — reservation_operating_hours  (many rows per company+location+dayOfWeek)
 *   3. Block periods      — reservation_block_periods    (soft-deleted)
 *
 * Visual language matches Company.tsx: `font-inter`, brand teal `#0F7679`,
 * primary buttons rounded `30px`, secondary buttons `1px solid #C9D7D8`, list
 * rows in `#EBF0F0`-bordered cards, `LoadingV2` overlay, `DropdownSearch`,
 * `PlusIcon` / `EditIconV2` / `DeleteIconV2`. Mobile breakpoint comes from
 * `useResponsive` (≤1024px) — every form row collapses from `calc(50% - 12px)`
 * to `100%` and the inner secondary tab strip wraps.
 *
 * Each sub-tab's rows are a list-kit ListScreen (server-paged, location filter,
 * `tab` in the URL); the add / edit forms stay page-owned and replace the list.
 */

type SubTab = "settings" | "hours" | "blocks";

interface LocationOption {
  id: string;
  locationCode?: string;
  name: string;
}

// Indices match the backend `DayOfWeek` enum (Monday=0 … Sunday=6) defined in
// dev-core/src/common/enums/reservation.enums.ts. Do NOT reorder to match
// `Date.prototype.getDay()` semantics (Sunday=0) without also adapting every
// `DAY_OF_WEEK_LABELS[row.dayOfWeek]` and dropdown id mapping below.
const DAY_OF_WEEK_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const t = (language: any, key: string, fallback?: string) =>
  (language && language[key]) || fallback || key;

const buttonPrimaryStyle: React.CSSProperties = {
  borderRadius: "30px",
  color: "#FFFFFF",
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: "#0F7679",
  padding: "10px 24px",
  border: "none",
  cursor: "pointer",
};

const buttonSecondaryStyle: React.CSSProperties = {
  borderRadius: "30px",
  padding: "10px 24px",
  border: "1px solid #C9D7D8",
  color: "#222525",
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: "transparent",
  cursor: "pointer",
};

const inputStyle: React.CSSProperties = {
  height: "48px",
  width: "100%",
  border: "1px solid #EBEBED",
  borderRadius: "12px",
  padding: "12px",
  fontSize: "14px",
  outline: "none",
  background: "transparent",
  fontFamily: "Inter, sans-serif",
};

const labelStyle: React.CSSProperties = {
  color: "#4A5353",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  fontFamily: "Inter, sans-serif",
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: "20px",
  fontWeight: 500,
  color: "#222525",
  lineHeight: "28px",
};

const SUB_TABS: SubTab[] = ["settings", "hours", "blocks"];

const hhmm = (value: string | null | undefined) => (value || "").slice(0, 5);

const Reservations: React.FC = () => {
  const { isMobile, isTouchLayout } = useResponsive();
  // Read before the list mounts so `key={subTab}` does not remount it on load.
  const [subTab, setSubTab] = useState<SubTab>(() => {
    const params: any = extractValues(location.search.split("&"));
    return SUB_TABS.includes(params.tab) ? params.tab : "settings";
  });
  const listRef = useRef<ListScreenHandle>(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<any>(null);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  // Back / Forward on this page: re-read ?tab; `key={subTab}` remounts the list.
  useEffect(() => {
    const path = location.pathname;
    const handlePopState = () => {
      if (location.pathname !== path) return;
      const params: any = extractValues(location.search.split("&"));
      setSubTab(SUB_TABS.includes(params.tab) ? params.tab : "settings");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const [locations, setLocations] = useState<LocationOption[]>([]);
  const locationsById = useMemo(() => {
    const map: Record<string, LocationOption> = {};
    locations.forEach((l) => (map[l.id] = l));
    return map;
  }, [locations]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getLocations(undefined, undefined, 1, 200, "");
        if (cancelled) return;
        const rows: LocationOption[] =
          res?.data?.data?.map((l: any) => ({
            id: l.id,
            locationCode: l.locationCode,
            name: l.name,
          })) ?? [];
        setLocations(rows);
      } catch (err) {
        // Silent — empty location list will surface in the dropdown.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const locationLabel = (locationId: string) =>
    locationsById[locationId]?.name ?? locationId;

  // ============================================================
  // Sub-tab 1: Settings
  // ============================================================
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  const [editingSettingsId, setEditingSettingsId] = useState<string | null>(null);
  const [settingsForm, setSettingsForm] = useState<ReservationSettingsPayload>({
    locationId: "",
    reservationsEnabled: true,
    maxPartySize: null,
    defaultDurationMinutes: 120,
    bookingWindowDays: 30,
    reservationCapacity: null,
    reservationsPaused: false,
    contactEmail: null,
    policy: null,
    emailsEnabled: true,
  });
  const [settingsLocationLabel, setSettingsLocationLabel] = useState<string>("");
  const [settingsLocationOpen, setSettingsLocationOpen] = useState(false);
  const settingsLocationRef = useRef<HTMLDivElement>(null);

  const fetchSettings = async (query: ListQuery) => {
    const res = await getReservationSettingsList(
      query.page,
      query.limit,
      query.filters.locationId || undefined
    );
    return { rows: res?.data ?? [], count: res?.count ?? 0 };
  };

  const onAddSettings = () => {
    setEditingSettingsId(null);
    setSettingsForm({
      locationId: "",
      reservationsEnabled: true,
      maxPartySize: null,
      defaultDurationMinutes: 120,
      bookingWindowDays: 30,
      reservationCapacity: null,
      reservationsPaused: false,
      contactEmail: null,
      policy: null,
      emailsEnabled: true,
    });
    setSettingsLocationLabel("");
    setShowSettingsForm(true);
  };

  const onEditSettings = (row: any) => {
    setEditingSettingsId(row.id);
    setSettingsForm({
      locationId: row.locationId,
      reservationsEnabled: !!row.reservationsEnabled,
      maxPartySize: row.maxPartySize ?? null,
      defaultDurationMinutes: row.defaultDurationMinutes ?? 120,
      bookingWindowDays: row.bookingWindowDays ?? 30,
      reservationCapacity: row.reservationCapacity ?? null,
      reservationsPaused: !!row.reservationsPaused,
      contactEmail: row.contactEmail ?? null,
      policy: row.policy ?? null,
      // Legacy rows predate the column: absent means enabled.
      emailsEnabled: row.emailsEnabled !== false,
    });
    setSettingsLocationLabel(locationLabel(row.locationId));
    setShowSettingsForm(true);
  };

  const onDeleteSettings = async (row: any) => {
    if (
      !window.confirm(
        t(language, "Delete reservation settings for this location?")
      )
    )
      return;
    setLoading(true);
    try {
      await deleteReservationSettings(row.id);
      showToast({
        message: t(language, "Reservation settings deleted"),
        type: "success",
      });
      listRef.current?.reload();
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to delete settings") });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsForm.locationId) {
      showToast({
        message: t(language, "Please select a location"),
        type: "warning",
      });
      return;
    }
    const contactEmail = (settingsForm.contactEmail ?? "").trim();
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      showToast({
        message: t(language, "Enter a valid contact email"),
        type: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      if (editingSettingsId) {
        await updateReservationSettings(editingSettingsId, settingsForm);
        showToast({
          message: t(language, "Reservation settings updated"),
          type: "success",
        });
      } else {
        await createReservationSettings(settingsForm);
        showToast({
          message: t(language, "Reservation settings created"),
          type: "success",
        });
      }
      setShowSettingsForm(false);
      setEditingSettingsId(null);
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to save settings") });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Sub-tab 2: Operating Hours
  // ============================================================
  const [showHoursForm, setShowHoursForm] = useState(false);
  const [editingHoursId, setEditingHoursId] = useState<string | null>(null);
  const [hoursForm, setHoursForm] = useState<ReservationOperatingHoursPayload>({
    locationId: "",
    dayOfWeek: 0,
    openTime: "09:00",
    closeTime: "22:00",
  });
  const [hoursLocationLabel, setHoursLocationLabel] = useState<string>("");
  const [hoursLocationOpen, setHoursLocationOpen] = useState(false);
  const hoursLocationRef = useRef<HTMLDivElement>(null);
  const [hoursDayLabel, setHoursDayLabel] = useState<string>("Monday");
  const [hoursDayOpen, setHoursDayOpen] = useState(false);
  const hoursDayRef = useRef<HTMLDivElement>(null);

  const fetchHours = async (query: ListQuery) => {
    const day = query.filters.dayOfWeek;
    const res = await getReservationOperatingHoursList(
      query.page,
      query.limit,
      query.filters.locationId || undefined,
      day === "" || day === undefined ? undefined : Number(day)
    );
    return { rows: res?.data ?? [], count: res?.count ?? 0 };
  };

  const onAddHours = () => {
    setEditingHoursId(null);
    setHoursForm({
      locationId: "",
      dayOfWeek: 0,
      openTime: "09:00",
      closeTime: "22:00",
    });
    setHoursLocationLabel("");
    setHoursDayLabel("Monday");
    setShowHoursForm(true);
  };

  const onEditHours = (row: any) => {
    setEditingHoursId(row.id);
    setHoursForm({
      locationId: row.locationId,
      dayOfWeek: row.dayOfWeek,
      openTime: (row.openTime || "").slice(0, 5),
      closeTime: (row.closeTime || "").slice(0, 5),
    });
    setHoursLocationLabel(locationLabel(row.locationId));
    setHoursDayLabel(DAY_OF_WEEK_LABELS[row.dayOfWeek] ?? "");
    setShowHoursForm(true);
  };

  const onDeleteHours = async (row: any) => {
    if (!window.confirm(t(language, "Delete this operating-hours entry?")))
      return;
    setLoading(true);
    try {
      await deleteReservationOperatingHours(row.id);
      showToast({
        message: t(language, "Operating hours deleted"),
        type: "success",
      });
      listRef.current?.reload();
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to delete operating hours") });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHours = async () => {
    if (
      !hoursForm.locationId ||
      hoursForm.dayOfWeek == null ||
      !hoursForm.openTime ||
      !hoursForm.closeTime
    ) {
      showToast({
        message: t(language, "Please complete all fields"),
        type: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      if (editingHoursId) {
        await updateReservationOperatingHours(editingHoursId, hoursForm);
        showToast({
          message: t(language, "Operating hours updated"),
          type: "success",
        });
      } else {
        await createReservationOperatingHours(hoursForm);
        showToast({
          message: t(language, "Operating hours created"),
          type: "success",
        });
      }
      setShowHoursForm(false);
      setEditingHoursId(null);
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to save operating hours") });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Sub-tab 3: Block periods
  // ============================================================
  const [showBlocksForm, setShowBlocksForm] = useState(false);
  const [editingBlocksId, setEditingBlocksId] = useState<string | null>(null);
  const [blocksForm, setBlocksForm] = useState<ReservationBlockPeriodPayload>({
    locationId: "",
    blockedFrom: "",
    blockedTo: "",
    reason: "",
  });
  const [blocksLocationLabel, setBlocksLocationLabel] = useState<string>("");
  const [blocksLocationOpen, setBlocksLocationOpen] = useState(false);
  const blocksLocationRef = useRef<HTMLDivElement>(null);

  const fetchBlocks = async (query: ListQuery) => {
    const res = await getReservationBlockPeriodsList(
      query.page,
      query.limit,
      query.filters.locationId || undefined
    );
    return { rows: res?.data ?? [], count: res?.count ?? 0 };
  };

  const onAddBlock = () => {
    setEditingBlocksId(null);
    setBlocksForm({
      locationId: "",
      blockedFrom: "",
      blockedTo: "",
      reason: "",
    });
    setBlocksLocationLabel("");
    setShowBlocksForm(true);
  };

  const onEditBlock = (row: any) => {
    setEditingBlocksId(row.id);
    // Convert ISO `2026-12-24T17:00:00.000Z` → input value "2026-12-24T17:00"
    const fromStr = row.blockedFrom ? new Date(row.blockedFrom).toISOString().slice(0, 16) : "";
    const toStr = row.blockedTo ? new Date(row.blockedTo).toISOString().slice(0, 16) : "";
    setBlocksForm({
      locationId: row.locationId,
      blockedFrom: fromStr,
      blockedTo: toStr,
      reason: row.reason ?? "",
    });
    setBlocksLocationLabel(locationLabel(row.locationId));
    setShowBlocksForm(true);
  };

  const onDeleteBlock = async (row: any) => {
    if (!window.confirm(t(language, "Delete this block period?"))) return;
    setLoading(true);
    try {
      await deleteReservationBlockPeriod(row.id);
      showToast({
        message: t(language, "Block period deleted"),
        type: "success",
      });
      listRef.current?.reload();
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to delete block period") });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBlock = async () => {
    if (
      !blocksForm.locationId ||
      !blocksForm.blockedFrom ||
      !blocksForm.blockedTo
    ) {
      showToast({
        message: t(language, "Please fill in location, from, and to"),
        type: "warning",
      });
      return;
    }
    if (new Date(blocksForm.blockedFrom) >= new Date(blocksForm.blockedTo)) {
      showToast({
        message: t(language, "Blocked-from must be earlier than blocked-to"),
        type: "warning",
      });
      return;
    }
    const payload: ReservationBlockPeriodPayload = {
      locationId: blocksForm.locationId,
      blockedFrom: new Date(blocksForm.blockedFrom).toISOString(),
      blockedTo: new Date(blocksForm.blockedTo).toISOString(),
      reason: blocksForm.reason ?? null,
    };
    setLoading(true);
    try {
      if (editingBlocksId) {
        await updateReservationBlockPeriod(editingBlocksId, payload);
        showToast({
          message: t(language, "Block period updated"),
          type: "success",
        });
      } else {
        await createReservationBlockPeriod(payload);
        showToast({
          message: t(language, "Block period created"),
          type: "success",
        });
      }
      setShowBlocksForm(false);
      setEditingBlocksId(null);
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to save block period") });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const locationColumn = (onEdit: (row: any) => void): ListColumn => ({
    key: "locationId",
    label: "Location",
    width: 240,
    hideable: false,
    render: cells.primary((row: any) => locationLabel(row.locationId)),
    value: (row: any) => locationLabel(row.locationId),
    mobile: "title",
    onClick: onEdit,
  });

  const locationFilter: ListFilter = {
    key: "locationId",
    label: "Location",
    kind: "select",
    options: locations.map((l) => ({ value: l.id, label: l.name })),
  };

  const rowActionsOf = (
    onEdit: (row: any) => void,
    onDelete: (row: any) => void
  ): ListRowAction[] => [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "inventory",
      onRun: onEdit,
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      onRun: onDelete,
    },
  ];

  /** Disabled wins over Paused, as the old "Disabled · Paused" line read. */
  const settingsStatus = (row: any) =>
    !row.reservationsEnabled
      ? { value: "Not Active", label: t(language, "Disabled") }
      : row.reservationsPaused
        ? { value: "Suspended", label: t(language, "Paused") }
        : { value: "Active", label: t(language, "Enabled") };

  const settingsColumns: ListColumn[] = [
    locationColumn(onEditSettings),
    {
      key: "status",
      label: "Status",
      width: 140,
      value: (row: any) => settingsStatus(row).label,
      render: (row: any) => <TagStatus {...settingsStatus(row)} />,
      mobile: "trailing",
    },
    {
      key: "defaultDurationMinutes",
      label: "Default duration (min)",
      width: 180,
      render: cells.text((row: any) => row.defaultDurationMinutes),
      value: (row: any) => `${t(language, "Default")}: ${row.defaultDurationMinutes}m`,
      mobile: "subtitle",
    },
    {
      key: "bookingWindowDays",
      label: "Booking window (days)",
      width: 180,
      render: cells.text((row: any) => row.bookingWindowDays),
      value: (row: any) => `${t(language, "Window")}: ${row.bookingWindowDays}d`,
      mobile: "subtitle",
    },
    {
      key: "emailsEnabled",
      label: "Send guest emails",
      width: 150,
      // Legacy rows predate the column: absent means enabled.
      value: (row: any) => (row.emailsEnabled === false ? t(language, "No") : t(language, "Yes")),
      mobile: "sheet",
    },
  ];

  const hoursColumns: ListColumn[] = [
    locationColumn(onEditHours),
    {
      key: "dayOfWeek",
      label: "Day of week",
      width: 160,
      value: (row: any) =>
        DAY_OF_WEEK_LABELS[row.dayOfWeek]
          ? t(language, DAY_OF_WEEK_LABELS[row.dayOfWeek])
          : `Day ${row.dayOfWeek}`,
      mobile: "subtitle",
    },
    { key: "openTime", label: "Open time", width: 120, value: (row: any) => hhmm(row.openTime), mobile: "hidden" },
    { key: "closeTime", label: "Close time", width: 120, value: (row: any) => hhmm(row.closeTime), mobile: "hidden" },
    {
      key: "openingHours",
      label: "Operating hours",
      width: 140,
      desktop: false,
      value: (row: any) => `${hhmm(row.openTime)} — ${hhmm(row.closeTime)}`,
      mobile: "trailing",
    },
  ];

  const blocksColumns: ListColumn[] = [
    locationColumn(onEditBlock),
    { key: "blockedFrom", label: "Blocked from", width: 170, value: (row: any) => formatDateTime(row.blockedFrom), mobile: "hidden" },
    { key: "blockedTo", label: "Blocked to", width: 170, value: (row: any) => formatDateTime(row.blockedTo), mobile: "hidden" },
    {
      key: "period",
      label: "Block periods",
      width: 300,
      desktop: false,
      value: (row: any) => `${formatDateTime(row.blockedFrom)} — ${formatDateTime(row.blockedTo)}`,
      mobile: "subtitle",
    },
    { key: "reason", label: "Reason", width: 280, value: (row: any) => row.reason ?? "", mobile: "sheet" },
  ];

  const dayFilter: ListFilter = {
    key: "dayOfWeek",
    label: "Day of week",
    kind: "select",
    options: DAY_OF_WEEK_LABELS.map((name, idx) => ({
      value: String(idx),
      label: t(language, name),
    })),
  };

  const listOfTab = {
    settings: {
      columns: settingsColumns,
      fetchRows: fetchSettings,
      filters: [locationFilter],
      rowActions: rowActionsOf(onEditSettings, onDeleteSettings),
      emptyState: {
        title: t(language, "Reservation settings"),
        desc: t(language, "No reservation settings yet. Add one per location to enable reservations."),
      },
    },
    hours: {
      columns: hoursColumns,
      fetchRows: fetchHours,
      filters: [locationFilter, dayFilter],
      rowActions: rowActionsOf(onEditHours, onDeleteHours),
      emptyState: {
        title: t(language, "Operating hours"),
        desc: t(language, "No operating hours configured yet."),
      },
    },
    blocks: {
      columns: blocksColumns,
      fetchRows: fetchBlocks,
      filters: [locationFilter],
      rowActions: rowActionsOf(onEditBlock, onDeleteBlock),
      emptyState: {
        title: t(language, "Block periods"),
        desc: t(
          language,
          "No block periods. Add one to temporarily stop accepting reservations (e.g. Christmas closure)."
        ),
      },
    },
  }[subTab];

  const formOpen =
    (subTab === "settings" && showSettingsForm) ||
    (subTab === "hours" && showHoursForm) ||
    (subTab === "blocks" && showBlocksForm);

  const changeSubTab = (key: SubTab) => {
    setShowSettingsForm(false);
    setShowHoursForm(false);
    setShowBlocksForm(false);
    if (key === subTab) return;
    setSubTab(key);
    // Replace, not push: this page never added history entries for its sub-tabs.
    window.history.replaceState(window.history.state, "", location.pathname + updateUrlParams({ tab: key, page: 1, limit: 50 }));
  };

  const renderList = () => (
    <ListScreen
      key={subTab}
      ref={listRef}
      language={language}
      columns={listOfTab.columns}
      fetchRows={listOfTab.fetchRows}
      search={false}
      filters={listOfTab.filters}
      rowActions={listOfTab.rowActions}
      emptyState={listOfTab.emptyState}
      extraUrlParams={{ tab: subTab }}
    />
  );

  // ============================================================
  // Render
  // ============================================================
  // Desktop list: a flex column so the table fills the height left. Forms and touch scroll the page.
  const fillHeight = !formOpen && !isTouchLayout;

  return (
    <div
      className="h-full"
      style={{
        width: "100%",
        ...(fillHeight
          ? { display: "flex", flexDirection: "column" }
          : { overflowY: "auto", paddingBottom: "24px" }),
      }}
    >
      <p
        className="font-inter"
        style={{
          fontSize: "20px",
          fontWeight: 500,
          lineHeight: "28px",
          color: "#222525",
          margin: "0 0 20px 0",
        }}
      >
        {t(language, "Reservations Setup")}
      </p>

      {/* Inner secondary tab strip */}
      <div
        className="flex items-center"
        style={{
          gap: "8px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {(
          [
            { key: "settings", label: t(language, "Settings") },
            { key: "hours", label: t(language, "Operating hours") },
            { key: "blocks", label: t(language, "Block periods") },
          ] as { key: SubTab; label: string }[]
        ).map((tab) => {
          const active = subTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className="font-inter cursor-pointer"
              onClick={() => changeSubTab(tab.key)}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "30px",
                border: active ? "1px solid #0F7679" : "1px solid #EBF0F0",
                color: active ? "#FFFFFF" : "#05292A",
                backgroundColor: active ? "#0F7679" : "transparent",
                lineHeight: "20px",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={{ position: "relative", minHeight: "120px" }}>
          <LoadingV2 />
        </div>
      )}

      {/* ============ Settings ============ */}
      {subTab === "settings" && (
        <>
          <div
            className="w-full flex items-center justify-between"
            style={{ marginBottom: formOpen ? "20px" : 0, gap: "12px", flexWrap: "wrap" }}
          >
            <div className="flex items-center" style={{ gap: "8px" }}>
              {showSettingsForm && (
                <button
                  type="button"
                  className="w-6 h-6 flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    setShowSettingsForm(false);
                    setEditingSettingsId(null);
                  }}
                  aria-label="Back"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                  }}
                >
                  <BackIcon />
                </button>
              )}
              <p style={sectionTitleStyle}>
                {t(language, "Reservation settings")}
              </p>
            </div>
            {!showSettingsForm && (
              <button
                type="button"
                className="font-inter flex items-center"
                onClick={onAddSettings}
                style={{ ...buttonPrimaryStyle, gap: "8px" }}
              >
                <PlusIcon />
                <span>{t(language, "Add settings")}</span>
              </button>
            )}
          </div>

          {!showSettingsForm && renderList()}

          {showSettingsForm && (
            <div
              className="flex items-center"
              style={{ gap: "24px", flexWrap: "wrap" }}
            >
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Location")}</p>
                <div style={{ height: "48px" }}>
                  <DropdownSearch
                    refKey="reservationSettingsLocation"
                    refs={{
                      reservationSettingsLocation: settingsLocationRef,
                    }}
                    openStates={{
                      reservationSettingsLocation: settingsLocationOpen,
                    }}
                    open={(k: string) =>
                      k === "reservationSettingsLocation" &&
                      setSettingsLocationOpen(true)
                    }
                    close={(k: string) =>
                      k === "reservationSettingsLocation" &&
                      setSettingsLocationOpen(false)
                    }
                    data={locations}
                    labelExtractor={(item: LocationOption) => item.name}
                    value={settingsLocationLabel}
                    setValue={setSettingsLocationLabel}
                    onSelect={(item: LocationOption) => {
                      setSettingsLocationLabel(item.name);
                      setSettingsForm((prev) => ({
                        ...prev,
                        locationId: item.id,
                      }));
                    }}
                    placeholder={t(language, "Select location")}
                  />
                </div>
              </div>
              <NumberField
                label={t(language, "Default duration (min)")}
                value={settingsForm.defaultDurationMinutes ?? 0}
                onChange={(n) =>
                  setSettingsForm((p) => ({ ...p, defaultDurationMinutes: n }))
                }
                isMobile={isMobile}
              />
              <NumberField
                label={t(language, "Booking window (days)")}
                value={settingsForm.bookingWindowDays ?? 0}
                onChange={(n) =>
                  setSettingsForm((p) => ({ ...p, bookingWindowDays: n }))
                }
                isMobile={isMobile}
              />
              <NumberField
                label={t(language, "Max party size")}
                value={(settingsForm.maxPartySize as number) ?? 0}
                onChange={(n) =>
                  setSettingsForm((p) => ({
                    ...p,
                    maxPartySize: n === 0 ? null : n,
                  }))
                }
                allowEmpty
                isMobile={isMobile}
              />
              <NumberField
                label={t(language, "Reservation capacity")}
                value={(settingsForm.reservationCapacity as number) ?? 0}
                onChange={(n) =>
                  setSettingsForm((p) => ({
                    ...p,
                    reservationCapacity: n === 0 ? null : n,
                  }))
                }
                allowEmpty
                isMobile={isMobile}
              />
              <YesNoField
                label={t(language, "Reservations enabled")}
                value={!!settingsForm.reservationsEnabled}
                onChange={(v) =>
                  setSettingsForm((p) => ({ ...p, reservationsEnabled: v }))
                }
              />
              <YesNoField
                label={t(language, "Paused")}
                value={!!settingsForm.reservationsPaused}
                onChange={(v) =>
                  setSettingsForm((p) => ({ ...p, reservationsPaused: v }))
                }
              />

              {/* Guest emails: who replies land with, the venue's own note in
                  the confirmation, and the per-location kill switch. */}
              <div
                style={{
                  width: "100%",
                  borderTop: "1px solid #EBEBED",
                  paddingTop: "16px",
                  marginTop: "4px",
                }}
              >
                <p style={{ ...labelStyle, fontWeight: 600, marginBottom: "4px" }}>
                  {t(language, "Guest emails")}
                </p>
                <p
                  className="font-inter text-[13px]"
                  style={{ color: "#898989", marginBottom: "12px" }}
                >
                  {t(
                    language,
                    "Confirmation and cancellation emails are sent to guests who leave an email address."
                  )}
                </p>
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Contact email")}</p>
                <input
                  type="email"
                  style={inputStyle}
                  value={settingsForm.contactEmail ?? ""}
                  onChange={(e) =>
                    setSettingsForm((p) => ({ ...p, contactEmail: e.target.value }))
                  }
                  placeholder={t(language, "e.g. bookings@yourvenue.com")}
                />
                <p className="font-inter text-[12px]" style={{ color: "#898989" }}>
                  {t(language, "Guest replies go to this address. Leave blank for none.")}
                </p>
              </div>
              <YesNoField
                label={t(language, "Send guest emails")}
                value={settingsForm.emailsEnabled !== false}
                onChange={(v) =>
                  setSettingsForm((p) => ({ ...p, emailsEnabled: v }))
                }
              />
              <div className="space-y-2" style={{ width: "100%" }}>
                <p style={labelStyle}>{t(language, "Policy")}</p>
                <textarea
                  style={{
                    ...inputStyle,
                    height: "112px",
                    resize: "vertical",
                    lineHeight: "20px",
                  }}
                  maxLength={2000}
                  value={settingsForm.policy ?? ""}
                  onChange={(e) =>
                    setSettingsForm((p) => ({ ...p, policy: e.target.value }))
                  }
                  placeholder={t(
                    language,
                    "e.g. Tables are held for 15 minutes past the booking time."
                  )}
                />
                <p className="font-inter text-[12px]" style={{ color: "#898989" }}>
                  {t(
                    language,
                    "Shown to the guest as \"A note from us\" in the confirmation email. Leave blank to hide it."
                  )}
                </p>
              </div>
              <div
                className="flex items-center justify-end"
                style={{ gap: "12px", marginTop: "12px", width: "100%" }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowSettingsForm(false);
                    setEditingSettingsId(null);
                  }}
                  style={buttonSecondaryStyle}
                >
                  {t(language, "Cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  style={buttonPrimaryStyle}
                >
                  {t(language, "Save")}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ Operating Hours ============ */}
      {subTab === "hours" && (
        <>
          <div
            className="w-full flex items-center justify-between"
            style={{ marginBottom: formOpen ? "20px" : 0, gap: "12px", flexWrap: "wrap" }}
          >
            <div className="flex items-center" style={{ gap: "8px" }}>
              {showHoursForm && (
                <button
                  type="button"
                  className="w-6 h-6 flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    setShowHoursForm(false);
                    setEditingHoursId(null);
                  }}
                  aria-label="Back"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                  }}
                >
                  <BackIcon />
                </button>
              )}
              <p style={sectionTitleStyle}>{t(language, "Operating hours")}</p>
            </div>
            {!showHoursForm && (
              <button
                type="button"
                className="font-inter flex items-center"
                onClick={onAddHours}
                style={{ ...buttonPrimaryStyle, gap: "8px" }}
              >
                <PlusIcon />
                <span>{t(language, "Add hours")}</span>
              </button>
            )}
          </div>

          {!showHoursForm && renderList()}

          {showHoursForm && (
            <div
              className="flex items-center"
              style={{ gap: "24px", flexWrap: "wrap" }}
            >
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Location")}</p>
                <div style={{ height: "48px" }}>
                  <DropdownSearch
                    refKey="reservationHoursLocation"
                    refs={{ reservationHoursLocation: hoursLocationRef }}
                    openStates={{
                      reservationHoursLocation: hoursLocationOpen,
                    }}
                    open={(k: string) =>
                      k === "reservationHoursLocation" &&
                      setHoursLocationOpen(true)
                    }
                    close={(k: string) =>
                      k === "reservationHoursLocation" &&
                      setHoursLocationOpen(false)
                    }
                    data={locations}
                    labelExtractor={(item: LocationOption) => item.name}
                    value={hoursLocationLabel}
                    setValue={setHoursLocationLabel}
                    onSelect={(item: LocationOption) => {
                      setHoursLocationLabel(item.name);
                      setHoursForm((prev) => ({
                        ...prev,
                        locationId: item.id,
                      }));
                    }}
                    placeholder={t(language, "Select location")}
                  />
                </div>
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Day of week")}</p>
                <div style={{ height: "48px" }}>
                  <DropdownSearch
                    refKey="reservationHoursDay"
                    refs={{ reservationHoursDay: hoursDayRef }}
                    openStates={{ reservationHoursDay: hoursDayOpen }}
                    open={(k: string) =>
                      k === "reservationHoursDay" && setHoursDayOpen(true)
                    }
                    close={(k: string) =>
                      k === "reservationHoursDay" && setHoursDayOpen(false)
                    }
                    data={DAY_OF_WEEK_LABELS.map((name, idx) => ({
                      id: idx,
                      name,
                    }))}
                    labelExtractor={(item: { id: number; name: string }) =>
                      item.name
                    }
                    value={hoursDayLabel}
                    setValue={setHoursDayLabel}
                    onSelect={(item: { id: number; name: string }) => {
                      setHoursDayLabel(item.name);
                      setHoursForm((prev) => ({ ...prev, dayOfWeek: item.id }));
                    }}
                    placeholder={t(language, "Select day")}
                  />
                </div>
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Open time")}</p>
                <input
                  type="time"
                  style={inputStyle}
                  value={hoursForm.openTime}
                  onChange={(e) =>
                    setHoursForm((p) => ({ ...p, openTime: e.target.value }))
                  }
                />
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Close time")}</p>
                <input
                  type="time"
                  style={inputStyle}
                  value={hoursForm.closeTime}
                  onChange={(e) =>
                    setHoursForm((p) => ({ ...p, closeTime: e.target.value }))
                  }
                />
              </div>
              <div
                className="flex items-center justify-end"
                style={{ gap: "12px", marginTop: "12px", width: "100%" }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowHoursForm(false);
                    setEditingHoursId(null);
                  }}
                  style={buttonSecondaryStyle}
                >
                  {t(language, "Cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveHours}
                  style={buttonPrimaryStyle}
                >
                  {t(language, "Save")}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ Block Periods ============ */}
      {subTab === "blocks" && (
        <>
          <div
            className="w-full flex items-center justify-between"
            style={{ marginBottom: formOpen ? "20px" : 0, gap: "12px", flexWrap: "wrap" }}
          >
            <div className="flex items-center" style={{ gap: "8px" }}>
              {showBlocksForm && (
                <button
                  type="button"
                  className="w-6 h-6 flex items-center justify-center cursor-pointer"
                  onClick={() => {
                    setShowBlocksForm(false);
                    setEditingBlocksId(null);
                  }}
                  aria-label="Back"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                  }}
                >
                  <BackIcon />
                </button>
              )}
              <p style={sectionTitleStyle}>{t(language, "Block periods")}</p>
            </div>
            {!showBlocksForm && (
              <button
                type="button"
                className="font-inter flex items-center"
                onClick={onAddBlock}
                style={{ ...buttonPrimaryStyle, gap: "8px" }}
              >
                <PlusIcon />
                <span>{t(language, "Add block period")}</span>
              </button>
            )}
          </div>

          {!showBlocksForm && renderList()}

          {showBlocksForm && (
            <div
              className="flex items-center"
              style={{ gap: "24px", flexWrap: "wrap" }}
            >
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Location")}</p>
                <div style={{ height: "48px" }}>
                  <DropdownSearch
                    refKey="reservationBlocksLocation"
                    refs={{ reservationBlocksLocation: blocksLocationRef }}
                    openStates={{
                      reservationBlocksLocation: blocksLocationOpen,
                    }}
                    open={(k: string) =>
                      k === "reservationBlocksLocation" &&
                      setBlocksLocationOpen(true)
                    }
                    close={(k: string) =>
                      k === "reservationBlocksLocation" &&
                      setBlocksLocationOpen(false)
                    }
                    data={locations}
                    labelExtractor={(item: LocationOption) => item.name}
                    value={blocksLocationLabel}
                    setValue={setBlocksLocationLabel}
                    onSelect={(item: LocationOption) => {
                      setBlocksLocationLabel(item.name);
                      setBlocksForm((prev) => ({
                        ...prev,
                        locationId: item.id,
                      }));
                    }}
                    placeholder={t(language, "Select location")}
                  />
                </div>
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Blocked from")}</p>
                <input
                  type="datetime-local"
                  style={inputStyle}
                  value={blocksForm.blockedFrom}
                  onChange={(e) =>
                    setBlocksForm((p) => ({ ...p, blockedFrom: e.target.value }))
                  }
                />
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Blocked to")}</p>
                <input
                  type="datetime-local"
                  style={inputStyle}
                  value={blocksForm.blockedTo}
                  onChange={(e) =>
                    setBlocksForm((p) => ({ ...p, blockedTo: e.target.value }))
                  }
                />
              </div>
              <div
                className="space-y-2"
                style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
              >
                <p style={labelStyle}>{t(language, "Reason")}</p>
                <input
                  type="text"
                  style={inputStyle}
                  value={blocksForm.reason ?? ""}
                  onChange={(e) =>
                    setBlocksForm((p) => ({ ...p, reason: e.target.value }))
                  }
                  placeholder={t(language, "e.g. Christmas closure")}
                />
              </div>
              <div
                className="flex items-center justify-end"
                style={{ gap: "12px", marginTop: "12px", width: "100%" }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowBlocksForm(false);
                    setEditingBlocksId(null);
                  }}
                  style={buttonSecondaryStyle}
                >
                  {t(language, "Cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveBlock}
                  style={buttonPrimaryStyle}
                >
                  {t(language, "Save")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Helper field components ----------

const NumberField: React.FC<{
  label: string;
  value: number;
  onChange: (n: number) => void;
  isMobile: boolean;
  allowEmpty?: boolean;
}> = ({ label, value, onChange, isMobile, allowEmpty }) => (
  <div
    className="space-y-2"
    style={{ width: isMobile ? "100%" : "calc(50% - 12px)" }}
  >
    <p style={labelStyle}>{label}</p>
    <input
      type="number"
      min={0}
      style={inputStyle}
      value={allowEmpty && (value === 0 || value == null) ? "" : value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "") {
          onChange(0);
          return;
        }
        const n = parseInt(v, 10);
        onChange(Number.isNaN(n) ? 0 : n);
      }}
    />
  </div>
);

const YesNoField: React.FC<{
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, value, onChange }) => (
  <div style={{ minWidth: "180px" }}>
    <p style={{ ...labelStyle, marginBottom: "8px" }}>{label}</p>
    <div className="flex items-center" style={{ gap: "16px" }}>
      <label
        className="flex items-center font-inter text-[14px]"
        style={{ gap: "8px", color: "#4A5353", cursor: "pointer" }}
      >
        <input
          type="radio"
          checked={value === true}
          onChange={() => onChange(true)}
          style={radioStyle(value === true)}
        />
        Yes
      </label>
      <label
        className="flex items-center font-inter text-[14px]"
        style={{ gap: "8px", color: "#4A5353", cursor: "pointer" }}
      >
        <input
          type="radio"
          checked={value === false}
          onChange={() => onChange(false)}
          style={radioStyle(value === false)}
        />
        No
      </label>
    </div>
  </div>
);

const radioStyle = (active: boolean): React.CSSProperties => ({
  width: 20,
  height: 20,
  minWidth: 20,
  minHeight: 20,
  margin: 0,
  flexShrink: 0,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  borderRadius: "50%",
  border: active ? "1px solid #0F7679" : "1px solid #C9D7D8",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  backgroundImage: active
    ? "radial-gradient(circle at center, #0F7679 0, #0F7679 5px, #ffffff 5.5px)"
    : "none",
});

export default Reservations;
